/**
 * Imports
 */
import { gsap } from 'gsap';
import { Observer } from 'gsap/Observer';
import CustomEvent from 'core/lib/CustomEvent';

/**
 * The main class
 */
export default new class Illustration extends CustomEvent {
	/**
	 * Collection of custom events name
	 *
	 * @type {Object} Collection of custom events name
	 */
	E = {
		RAF: 'raf',
	};

	/**
	 * @type {number}
	 */
	INERTIA = 0.09;

	/**
	 * Returns a promise to make sure all is ready
	 *
	 * @return {Promise} Promise
	 */
	init() {
		this.$illustration = document.querySelector( '#illustration' );
		this.$shapes = this.$illustration.childNodes;
		this.$rotate_continuous = this.$illustration.querySelectorAll( '[data-name*="rotationcontinuous"]' );
		this.$rotate_random = this.$illustration.querySelectorAll( '[data-name*="rotationrandom"]' );
		this.$wind = this.$illustration.querySelectorAll( '[data-name*="wind"]' );
		this.currentPosValues = { x: 0, y: 0 };

		this.#initShapes();
		this.#bindEvents();
		this.#initAutoMove();
		this.#initRotateContinuous();
		this.$rotate_random.forEach( item => {
			this.#initRotateRandom( item, 0.3 );
		} );
		this.#initWind();
	}

	#initShapes() {
		this.shapes = [];
		this.$shapes.forEach( ( shape, index ) => {
			const posValues = shape.getAttribute( 'data-name' ).split( '_' );
			this.shapes.push( {
				shape: shape,
				percentX: parseInt( posValues[ 0 ].substring( 1 ) ),
				percentY: ( posValues[ 1 ].substring( 1 ) ),
			} );
		} );

	}

	#bindEvents() {
		Observer.create( {
			target: window, // can be any element (selector text is fine)
			type: 'pointer,touch', // comma-delimited list of what to listen for
			onMove: this.#onMove.bind( this ), onDrag: this.#onMove.bind( this ),
		} );
	}

	#initAutoMove() {
		this.autoMoveTimeline = gsap.timeline( {
			yoyo: false,
			repeat: -1,
			repeatDelay: 1,
			repeatRefresh: true,
			onUpdate: this.#update.bind( this ),
		} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0,
				y: 0,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0.5,
				y: 0.5,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0,
				y: 1,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0.5,
				y: 0.5,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 1,
				y: 1,
			} )
			.to( this.currentPosValues, {
				duration: 2,
				ease: 'none',
				x: 0.5,
				y: 0.5,
			} )
			.to( this.currentPosValues, {
				duration: 0.7,
				ease: 'none',
				x: 1,
				y: 0,
			} )
			.to( this.currentPosValues, {
				duration: 0.7,
				ease: 'none',
				x: 0.5,
				y: 0.5,
			} );
	}

	#initRotateContinuous() {
		gsap.to( this.$rotate_continuous, {
			duration: 10,
			ease: 'none',
			repeat: -1,
			rotation: ( index ) => index % 2 ? 360 : -360,
			transformOrigin: 'center',
		} );
	}

	#initRotateRandom( item, delay = Math.random() * 6 + 6 ) {
		gsap.to( item, {
			duration: 1.2,
			ease: 'back.out',
			delay: delay,
			onComplete: this.#initRotateRandom.bind( this, item ),
			rotation: () => '+=' + ( Math.random() > 0.5 ? 90 : -90 ),
			transformOrigin: 'center',
		} );
	}

	#initWind() {
		gsap.to( this.$wind, {
			duration: () => Math.random() + 1,
			ease: 'quad.inOut',
			repeat: -1,
			repeatRefresh: true,
			rotation: () => ( Math.random() - 0.5 ) * 15,
			transformOrigin: 'bottom',
		} );
	}

	#onMove( ref ) {
		this.currentPosValues.x = ( ( ref.x / window.innerWidth ) - 0.5 );
		this.currentPosValues.y = ( ref.y / window.innerHeight ) - 0.5;
		this.#update();
		if ( this.autoMoveDelay ) {
			this.autoMoveDelay.kill();
			this.autoMoveTimeline.pause();
		}
		this.autoMoveDelay = gsap.delayedCall( 1, () => {
			this.autoMoveTimeline.invalidate().resume();
		} );
	}

	#update() {
		this.shapes.forEach( ( shape, index ) => {
			gsap.to( shape.shape, {
				duration: 1,
				x: this.currentPosValues.x * shape.percentX,
				y: this.currentPosValues.y * shape.percentY,
			} );
		} );
	}

};
